import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import './InfoTechBar.css';

/* ─── Utilities ─────────────────────────────────────────────────────── */
const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || navigator.vendor || window.opera
  );

/* ─── Text Decode Hook ───────────────────────────────────────────────── */
const DECODE_CHARS = 'アイウエ0123456789ABCDEF#$%&@';

function useDecodeText(target, active, charDelay = 45) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) { setDisplay(target); return; }

    let resolved = 0;
    let frame = 0;
    const len = target.length;

    function tick() {
      frame++;
      const framesPerChar = Math.max(1, Math.round(charDelay / 16));
      if (frame % framesPerChar === 0 && resolved < len) resolved++;

      let str = '';
      for (let i = 0; i < len; i++) {
        if (i < resolved || target[i] === ' ') {
          str += target[i];
        } else {
          str += DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)];
        }
      }
      setDisplay(str);
      if (resolved < len) rafRef.current = requestAnimationFrame(tick);
    }

    setDisplay(target.replace(/[^ ]/g, () => DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)]));
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, target, charDelay]);

  return display;
}

/* ─── Decoded Link ───────────────────────────────────────────────────── */
function DecodedLink({ to, label, delay = 0, panelOpen, onClick, state }) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!panelOpen) { setActive(false); return; }
    const t = setTimeout(() => setActive(true), delay);
    return () => clearTimeout(t);
  }, [panelOpen, delay]);

  const text = useDecodeText(label, active, 38);

  return (
    <Link to={state ? { pathname: to, state } : to} className="panel-link" onClick={onClick}>
      <span className="panel-link-bracket">[</span>
      <span className="panel-link-text">{text}</span>
      <span className="panel-link-bracket">]</span>
    </Link>
  );
}

/* ─── Section Data ───────────────────────────────────────────────────── */
const SECTIONS = {
  computer: {
    id: 'computer',
    title: 'מחשוב //',
    subtitle: 'IT DEPARTMENT',
    links: [
      { to: '/information-technology/troubleshooting-guides', label: 'מדריכי פתרון תקלות' },
      { to: '/information-technology/technology-news', label: 'חדשות טכנולוגיה' },
      { to: '/information-technology/building-computers', label: 'הרכבות מחשבים' },
    ],
  },
  logo: {
    id: 'logo',
    title: 'מערכת ראשי //',
    subtitle: 'MAIN SYSTEM',
    links: [
      { to: '/information-technology/works-with', label: 'שירותים וספקים' },
      { to: '/thanks', label: 'תודות', state: { from: 'information-technology' } },
      { to: '/contact-us', label: 'צור קשר', state: { from: 'information-technology' } },
    ],
  }
};

/* ─── Command Panel ──────────────────────────────────────────────────── */
function CommandPanel({ section, open, onMouseEnter, onMouseLeave }) {
  const data = SECTIONS[section];
  return (
    <div
      className={`command-panel ${open ? 'panel-open' : ''}`}
      aria-hidden={!open}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="panel-bg-grid" aria-hidden="true" />
      {data && (
        <div className="panel-inner">
          <div className="panel-header">
            <span className="panel-title">{data.title}</span>
            <span className="panel-subtitle">{data.subtitle}</span>
          </div>
          <div className="panel-links">
            {data.links.map((lk, i) => (
              <DecodedLink
                key={lk.to}
                to={lk.to}
                label={lk.label}
                delay={i * 90}
                panelOpen={open}
                onClick={onMouseLeave}
                state={lk.state}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── InfoTechBar ──────────────────────────────────────────────────────── */
function InfoTechBar() {
  const [activeSection, setActiveSection] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(null);
  const [mobileClosing, setMobileClosing] = useState(false);
  const [showBar, setShowBar] = useState(true);

  const timers = useRef({});
  const barRef = useRef(null);
  const touchStartY = useRef(0);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    if (mobile) {
      document.body.classList.add('mobile-device');
    } else {
      document.body.classList.remove('mobile-device');
    }
  }, []);

  const clear = () => Object.values(timers.current).forEach(clearTimeout);

  const openPanel = useCallback((section) => {
    clear();
    if (activeSection === section && panelOpen) return;
    if (panelOpen && activeSection !== section) {
      setActiveSection(section);
      return;
    }
    setActiveSection(section);
    timers.current.open = setTimeout(() => setPanelOpen(true), 0);
  }, [activeSection, panelOpen]);

  const closePanel = useCallback(() => {
    clear();
    timers.current.close = setTimeout(() => {
      setPanelOpen(false);
      timers.current.clear = setTimeout(() => setActiveSection(null), 320);
    }, 350);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(timers.current.close);
    clearTimeout(timers.current.clear);
  }, []);

  const handleScroll = useCallback(() => {
    const el = document.querySelector('.content.has-navbar');
    if (!el) return;
    const y = el.scrollTop;
    const up = y < lastScrollY.current || y < 50;
    if (showBar !== up) setShowBar(up);
    if (!up) { setPanelOpen(false); setActiveSection(null); }
    lastScrollY.current = y;
  }, [showBar]);

  const handleClickOutside = useCallback((e) => {
    if (!barRef.current) return;
    if (barRef.current.contains(e.target)) return;
    setPanelOpen(false);
    setActiveSection(null);
    clear();
  }, []);

  const handleTouchStart = (e) => (touchStartY.current = e.touches[0].clientY);
  const handleTouchMove = useCallback((e) => {
    if (!mobileMenu) return;
    if (e.touches[0].clientY - touchStartY.current > 70) closeMobileMenu();
  }, [mobileMenu]);

  const openMobileMenu = (section) => {
    if (mobileMenu === section) { closeMobileMenu(); return; }
    if (mobileMenu) {
      closeMobileMenu();
      setTimeout(() => setMobileMenu(section), 350);
    } else {
      setMobileMenu(section);
    }
  };

  const closeMobileMenu = () => {
    if (!mobileMenu) return;
    setMobileClosing(true);
    setTimeout(() => { setMobileMenu(null); setMobileClosing(false); }, 350);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    const el = document.querySelector('.content.has-navbar');
    if (el) el.addEventListener('scroll', handleScroll);
    else window.addEventListener('scroll', handleScroll);

    if (isMobile) {
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (el) el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      clear();
    };
  }, [isMobile, handleClickOutside, handleTouchMove, handleScroll]);

  const renderIcon = (sectionKey, src, alt, to) => {
    const isActive = activeSection === sectionKey && panelOpen;
    return (
      <li
        key={sectionKey}
        className={`bar-icon ${isActive ? 'icon-active' : ''}`}
        onMouseEnter={() => !isMobile && openPanel(sectionKey)}
        onMouseLeave={() => !isMobile && closePanel()}
      >
        <Link
          to={to}
          className="menu-icon"
          onClick={(e) => {
            if (isMobile) { e.preventDefault(); openMobileMenu(sectionKey); }
          }}
          aria-expanded={isActive}
        >
          <span className="icon-ring" />
          <img src={src} alt={alt} />
          <span className="icon-glow" />
          <span className="fx-scan" aria-hidden="true" />
        </Link>
      </li>
    );
  };

  const items = useMemo(() => {
    const list = [];
    list.push(renderIcon('computer', '/images/computer.png', 'IT Department', '/information-technology/InfoTechDepartment'));
    list.push(renderIcon('logo', '/images/logo.png', 'Logo', '/'));
    return list;
  }, [activeSection, panelOpen, isMobile, openPanel, closePanel]);

  const MobileSheet = () => {
    if (!isMobile || !mobileMenu) return null;
    const data = SECTIONS[mobileMenu];
    return (
      <>
        <div className="mobile-overlay" onClick={closeMobileMenu} />
        <div className={`mobile-sheet ${mobileClosing ? 'closing' : ''}`}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
          <div className="swipe-indicator" />
          <button className="close-sheet-btn" onClick={closeMobileMenu} aria-label="סגור">✕</button>
          <div className="sheet-header">
            <span className="sheet-title">{data?.title}</span>
            <span className="sheet-subtitle">{data?.subtitle}</span>
          </div>
          {[{ to: '/', label: 'חזור לדף הראשי' }, ...(data?.links || [])].map((lk, i) => (
            <Link key={lk.to + i} to={lk.state ? { pathname: lk.to, state: lk.state } : lk.to} className="sheet-link" onClick={closeMobileMenu}
              style={{ animationDelay: `${i * 80}ms` }}>
              <span className="sheet-link-bracket">[</span>
              {lk.label}
              <span className="sheet-link-bracket">]</span>
            </Link>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <div ref={barRef} className={`sidebar ${showBar ? 'visible' : 'hidden'}`}>
        <div className="bar-grid" aria-hidden="true" />
        <div className="bar-fog" aria-hidden="true" />
        <div className="bar-horizon" aria-hidden="true" />
        <div className="bar-scanlines" aria-hidden="true" />

        <ul className="icon-strip">{items}</ul>

        {!isMobile && (
          <CommandPanel
            section={activeSection}
            open={panelOpen}
            onMouseEnter={cancelClose}
            onMouseLeave={closePanel}
          />
        )}
        {isMobile && mobileMenu && createPortal(
          <MobileSheet />,
          document.body
        )}
      </div>
    </>
  );
}

export default InfoTechBar;
